import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type PendingRegistration = {
  email: string;
  business_name: string;
  created_at: string;
  status: string;
};

export const WaitingApproval = () => {
  const [pendingRegistration, setPendingRegistration] = useState<PendingRegistration | null>(null);

  useEffect(() => {
    // Get the most recent pending registration
    const pendingRegistrations = JSON.parse(
      localStorage.getItem('pendingRegistrations') || '[]'
    ) as PendingRegistration[];
    
    if (pendingRegistrations.length > 0) {
      // Sort by creation date (newest first)
      const sorted = [...pendingRegistrations].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setPendingRegistration(sorted[0]);
    }
  }, []);

  if (!pendingRegistration) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No Pending Registration</h1>
          <p className="text-gray-600 mb-6">
            We couldn't find any pending business registration. Please try registering again.
          </p>
          <Link
            to="/register"
            className="bg-navy-blue text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Registration
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <svg
            className="mx-auto h-16 w-16 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">
            Registration Submitted for Review
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Thank you for registering your business,{' '}
            <span className="font-medium">{pendingRegistration.business_name}</span>.
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                What happens next?
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Our team will review your application and get back to you at{' '}
                  <span className="font-medium">{pendingRegistration.email}</span> within 1-2 business days.
                </p>
                <p className="mt-2">
                  Once approved, you'll receive an email with instructions to complete your registration.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <a
              href="mailto:support@catchoftheday.com"
              className="font-medium text-navy-blue hover:text-blue-700"
            >
              Contact support
            </a>
          </p>
        </div>

        <div className="mt-6">
          <Link
            to="/"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-navy-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WaitingApproval;
